import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, X, PackagePlus, Settings2, WifiOff } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { measureTypeOf } from './shared/measureType';
import { beverageProductsService, siteInventoryService, measureDefinitionsService } from '../services';
import AddFromMasterModal from './AddFromMasterModal';
import { useToast } from '../hooks/use-toast';
import { getApiError } from '../lib/utils';

/**
 * Site Inventory Setup — floating window opened from the Stocktake wizard
 * header. iter340 v5 user directive (production):
 *
 * > "Create a new button next to buttons in header visible for all screens
 * >  within wizard, call it 'Site Inventory Setup'. Within this expose a
 * >  floating window of all products assigned to this site, populated with
 * >  yields and units of measure. The yields and units of measure
 * >  information should be pulled from Inventory Master, and if there is a
 * >  site specific override then that value should be exposed instead. The
 * >  table should indicate if the yields and units of measure are pulled
 * >  from Inventory Master or are Site Specific Overrides. At the top of
 * >  the table, include the usual product search fields, filter buttons
 * >  for category and subcategory, and a button to 'Add product from
 * >  Inventory Master'."
 *
 * Works online (fetches site_inventory + master products + site overrides)
 * and offline (reads everything from the offline package).
 */
export default function SiteInventorySetupModal({
  open,
  onOpenChange,
  siteId,
  siteName,
  isOfflineMode = false,
  offlineData = null,
  onOfflineMasterAssign = null,
  onOfflineMeasureChange = null,
  onOnlineRefresh = null,
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [siteInventory, setSiteInventory] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [siteOverrides, setSiteOverrides] = useState([]);  // SiteProductMeasure rows
  const [measureDefs, setMeasureDefs] = useState([]); // org-wide MeasureDefinitions for serving-unit dropdown
  const [query, setQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [addFromMasterOpen, setAddFromMasterOpen] = useState(false);
  // iter340 v6 — per-row inline edit state. Map of pid → { measure_ml, nominal_servings, dirty, saving }.
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      if (isOfflineMode) {
        // Offline path — everything ships in the package.
        const pkg = offlineData || {};
        setSiteInventory(pkg.site_inventory || []);
        setAllProducts(pkg.products || []);
        setSiteOverrides(pkg.site_product_measures || []);
        setMeasureDefs(pkg.measure_definitions || []);
      } else {
        const [inv, products, overrides, defs] = await Promise.all([
          siteInventoryService.listInventory(siteId),
          beverageProductsService.listAll({ status: 'active' }),
          siteInventoryService.getProductMeasures(siteId).catch(() => []),
          measureDefinitionsService.list().catch(() => []),
        ]);
        setSiteInventory(Array.isArray(inv) ? inv : (inv?.items || inv?.beverage_inventory || []));
        setAllProducts(Array.isArray(products) ? products : (products?.items || products?.products || []));
        setSiteOverrides(Array.isArray(overrides) ? overrides : []);
        setMeasureDefs(
          // iter420k — Sort measure defs alphabetically (case-insensitive)
          // so the dropdown order is predictable across the module.
          (Array.isArray(defs) ? defs : []).slice().sort((a, b) =>
            (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' })
          )
        );
      }
    } catch (err) {
      toast({
        title: 'Failed to load site inventory',
        description: getApiError(err, 'Please try again'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [siteId, isOfflineMode, offlineData, toast]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Index master products by id for quick lookup.
  const productById = useMemo(() => {
    const map = {};
    for (const p of allProducts) map[String(p.id)] = p;
    return map;
  }, [allProducts]);

  // Index site overrides by beverage_product_id.
  const overrideByPid = useMemo(() => {
    const map = {};
    for (const o of siteOverrides) {
      if (o?.beverage_product_id != null) map[String(o.beverage_product_id)] = o;
    }
    return map;
  }, [siteOverrides]);

  // Resolve the master "default" yield + unit for a product.
  const resolveMaster = (product) => {
    if (!product) return { defaultUnit: null, defaultBaseUnit: null, defaultMeasureMl: null, defaultMeasureLabel: null, masterServings: null };
    // Default unit: prefer is_default_count_unit, fall back to first.
    const units = product.units || [];
    const defaultUnit = units.find(u => u.is_default_count_unit) || units[0] || null;
    // Default yield: prefer is_default, fall back to first.
    const yields = product.yields || [];
    const defaultYield = yields.find(y => y.is_default) || yields[0] || null;
    return {
      defaultUnit: defaultUnit?.unit_type || null,
      defaultBaseUnit: defaultUnit?.base_unit || null,
      defaultMeasureMl: defaultYield?.ml_per_serving ?? defaultYield?.nominal_ml_per_serving ?? null,
      defaultMeasureLabel: defaultYield?.yield_type || null,
      masterServings: defaultYield?.servings_per_unit ?? defaultYield?.nominal_servings_per_unit ?? null,
    };
  };

  // Build the unified rows.
  const rows = useMemo(() => {
    return siteInventory.map(inv => {
      const pid = String(inv.beverage_product_id ?? inv.id);
      const product = productById[pid] || null;
      const master = resolveMaster(product);
      const override = overrideByPid[pid] || null;
      const siteCountUnit = override?.count_unit_id
        ? (product?.units || []).find(u => u.id === override.count_unit_id)?.unit_type
        : null;
      const overrideMl = override?.measure_ml ?? null;
      const overrideServings = override?.nominal_servings_per_unit ?? null;
      // Decide effective values (site override beats master).
      const effectiveMl = overrideMl != null ? overrideMl : master.defaultMeasureMl;
      const effectiveServings = overrideServings != null ? overrideServings : master.masterServings;
      // Source labels (separate for measure vs yield because either can be overridden).
      const measureSource = overrideMl != null ? 'Site-Specific Override' : 'Inventory Master';
      const yieldSource = overrideServings != null ? 'Site-Specific Override' : 'Inventory Master';
      // 2026-11 fix — this table used to label EVERY product's measure
      // value "X ml" regardless of what the number actually represents.
      // For a mass-based packaged snack (base_unit g/kg) the stored
      // number is grams, not millilitres (e.g. a 40g crisp packet
      // showed as "40 ml"); for a count product (each/packet/pack) the
      // "measure (ml) / yield" concept — a serving-size dilution ratio —
      // doesn't apply at all (there's nothing to convert into servings).
      const measureType = measureTypeOf(master.defaultBaseUnit);
      return {
        id: pid,
        product_id: Number(pid),
        product_name: product?.product_name || inv.product_name || `Product ${pid}`,
        product_code: product?.product_code || null,
        brand: product?.brand || null,
        category: product?.category || null,
        subcategory: product?.subcategory || null,
        default_unit: siteCountUnit || master.defaultUnit,
        default_unit_is_site: !!siteCountUnit,
        measure_type: measureType,
        master_measure_ml: master.defaultMeasureMl,
        master_measure_label: master.defaultMeasureLabel,
        master_servings: master.masterServings,
        override_measure_ml: overrideMl,
        override_measure_label: override?.measure_label || null,
        override_servings: overrideServings,
        effective_measure_ml: effectiveMl,
        effective_servings: effectiveServings,
        measure_source: measureSource,
        yield_source: yieldSource,
        any_override: overrideMl != null || overrideServings != null,
      };
    }).sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));
  }, [siteInventory, productById, overrideByPid]);

  // Available filter options (built from the site's assigned products only).
  const availableCategories = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.category) set.add(r.category); });
    return [...set].sort();
  }, [rows]);
  const availableSubcategories = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.subcategory) set.add(r.subcategory); });
    return [...set].sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      if (selectedCategories.length > 0 && !selectedCategories.includes(r.category)) return false;
      if (selectedSubcategories.length > 0 && !selectedSubcategories.includes(r.subcategory)) return false;
      if (query) {
        const q = query.toLowerCase();
        const hay = `${r.product_name || ''} ${r.product_code || ''} ${r.brand || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, selectedCategories, selectedSubcategories, query]);

  const fmtMl = (v, measureType) => {
    if (v == null) return '—';
    if (measureType === 'count') return '—'; // n/a — no serving-size dilution for a count product
    if (measureType === 'mass') return `${Number(v).toFixed(1)} g`;
    return `${Number(v).toFixed(1)} ml`;
  };
  const fmtServings = (v, measureType) => {
    if (measureType === 'count') return '—'; // n/a — 1 unit = 1 serving, no yield ratio
    return v == null ? '—' : `${Number(v).toFixed(2)} / unit`;
  };

  // iter340 v8 — `beginEdit` only seeds the fields the user is allowed
  // to change: yield (servings/unit) and the default serving unit
  // (measure_label, sourced from MeasureDefinitions). Measure (ml) is
  // no longer editable here — it's now derived from the chosen serving
  // unit (Pint → 568ml, Glass → 150ml, …).
  const beginEdit = useCallback((row) => {
    setEdits(prev => ({
      ...prev,
      [row.product_id]: {
        nominal_servings: row.override_servings != null ? String(row.override_servings) : (row.master_servings != null ? String(row.master_servings) : ''),
        measure_label: row.override_measure_label || row.master_measure_label || '',
        dirty: false,
        saving: false,
      },
    }));
  }, []);
  const cancelEdit = useCallback((pid) => {
    setEdits(prev => { const n = { ...prev }; delete n[pid]; return n; });
  }, []);
  const updateEdit = useCallback((pid, field, value) => {
    setEdits(prev => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), [field]: value, dirty: true },
    }));
  }, []);

  const saveEdit = useCallback(async (row) => {
    const e = edits[row.product_id];
    if (!e) return;
    const servings = e.nominal_servings.trim() === '' ? null : Number(e.nominal_servings);
    const measureLabel = (e.measure_label || '').trim() || null;
    // Derive measure_ml from the chosen MeasureDefinition (so the
    // engine still has a numeric ml signal). When the user clears the
    // serving unit, drop measure_ml too.
    let measureMl = null;
    if (measureLabel) {
      const def = measureDefs.find(d => (d.name || '').toLowerCase() === measureLabel.toLowerCase());
      if (def?.default_ml != null) measureMl = Number(def.default_ml);
    }
    if (servings == null && !measureLabel) {
      toast({ title: 'Provide at least one value', description: 'Choose a default serving unit and/or enter a yield, or use "Reset to master" to clear an override.', variant: 'destructive' });
      return;
    }
    if (servings != null && (Number.isNaN(servings) || servings <= 0)) {
      toast({ title: 'Invalid yield', description: 'Yield (servings/unit) must be a positive number.', variant: 'destructive' });
      return;
    }
    setEdits(prev => ({ ...prev, [row.product_id]: { ...prev[row.product_id], saving: true } }));
    try {
      const payload = {
        measure_ml: measureMl,
        measure_label: measureLabel,
        nominal_servings_per_unit: servings,
      };
      if (isOfflineMode) {
        if (onOfflineMeasureChange) {
          await onOfflineMeasureChange({ beverage_product_id: row.product_id, action: 'upsert', ...payload });
        }
        // Reflect locally — patch siteOverrides so the table updates immediately.
        setSiteOverrides(prev => {
          const next = prev.filter(o => Number(o.beverage_product_id) !== Number(row.product_id));
          next.push({ beverage_product_id: row.product_id, ...payload });
          return next;
        });
        toast({ title: 'Override queued', description: `${row.product_name} — will sync when you reconnect.` });
      } else {
        await siteInventoryService.upsertProductMeasure(siteId, row.product_id, payload);
        toast({ title: 'Override saved', description: `${row.product_name} — site-specific values applied.` });
        await load();
      }
      cancelEdit(row.product_id);
    } catch (err) {
      toast({ title: 'Failed to save override', description: getApiError(err, 'Try again'), variant: 'destructive' });
      setEdits(prev => ({ ...prev, [row.product_id]: { ...prev[row.product_id], saving: false } }));
    }
  }, [edits, measureDefs, isOfflineMode, onOfflineMeasureChange, siteId, toast, load, cancelEdit]);

  const resetToMaster = useCallback(async (row) => {
    if (row.override_measure_ml == null && row.override_servings == null) {
      cancelEdit(row.product_id);
      return;
    }
    setEdits(prev => ({ ...prev, [row.product_id]: { ...(prev[row.product_id] || {}), saving: true } }));
    try {
      if (isOfflineMode) {
        if (onOfflineMeasureChange) {
          await onOfflineMeasureChange({ beverage_product_id: row.product_id, action: 'delete' });
        }
        setSiteOverrides(prev => prev.filter(o => Number(o.beverage_product_id) !== Number(row.product_id)));
        toast({ title: 'Reset queued', description: `${row.product_name} — will revert to master on next sync.` });
      } else {
        await siteInventoryService.deleteProductMeasure(siteId, row.product_id);
        toast({ title: 'Reverted to master', description: `${row.product_name} — site override removed.` });
        await load();
      }
      cancelEdit(row.product_id);
    } catch (err) {
      toast({ title: 'Failed to reset', description: getApiError(err, 'Try again'), variant: 'destructive' });
      setEdits(prev => ({ ...prev, [row.product_id]: { ...prev[row.product_id], saving: false } }));
    }
  }, [isOfflineMode, onOfflineMeasureChange, siteId, toast, load, cancelEdit]);

  const toggleCat = (c) => setSelectedCategories(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const toggleSub = (s) => setSelectedSubcategories(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const clearFilters = () => { setSelectedCategories([]); setSelectedSubcategories([]); setQuery(''); };

  const assignedProductIds = useMemo(
    () => rows.map(r => r.product_id).filter(Boolean),
    [rows]
  );

  const offlineProductsForModal = isOfflineMode ? (offlineData?.products || []) : null;

  const handleAssigned = () => {
    // Online — refresh both this modal and let the parent know.
    load();
    if (onOnlineRefresh) onOnlineRefresh();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl" data-testid="site-inventory-setup-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-4 h-4" />
              Site Inventory Setup
              {siteName && <span className="text-sm font-normal text-muted-foreground ml-1">— {siteName}</span>}
            </DialogTitle>
            <DialogDescription>
              All products assigned to this client, with their measures and yields.
              Values come from the Inventory Master unless a Site-Specific Override is in place.
            </DialogDescription>
            {isOfflineMode && (
              <div
                className="flex items-center gap-2 px-2.5 py-1.5 mt-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs"
                data-testid="site-inv-setup-offline-banner"
              >
                <WifiOff className="w-3.5 h-3.5" />
                Offline mode — showing cached site inventory. Adding new products queues them for sync.
              </div>
            )}
          </DialogHeader>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 border-b pb-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name, code or brand..."
                className="pl-9 h-9"
                data-testid="site-inv-setup-search"
              />
            </div>
            {availableCategories.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 text-xs" data-testid="site-inv-setup-cat-btn">
                    Category{selectedCategories.length > 0 ? ` (${selectedCategories.length})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {availableCategories.map(c => (
                      <label key={c} className="flex items-center gap-2 text-sm px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={selectedCategories.includes(c)} onCheckedChange={() => toggleCat(c)} />
                        {c}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {availableSubcategories.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 text-xs" data-testid="site-inv-setup-subcat-btn">
                    Subcategory{selectedSubcategories.length > 0 ? ` (${selectedSubcategories.length})` : ''}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {availableSubcategories.map(s => (
                      <label key={s} className="flex items-center gap-2 text-sm px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={selectedSubcategories.includes(s)} onCheckedChange={() => toggleSub(s)} />
                        {s}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {(selectedCategories.length + selectedSubcategories.length + (query ? 1 : 0)) > 0 && (
              <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={clearFilters} data-testid="site-inv-setup-clear">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
            <div className="ml-auto">
              <Button
                size="sm"
                className="h-9"
                onClick={() => setAddFromMasterOpen(true)}
                data-testid="site-inv-setup-add-from-master"
              >
                <PackagePlus className="w-3.5 h-3.5 mr-1.5" />
                Add product from Inventory Master
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="max-h-[60vh] overflow-y-auto border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-500" data-testid="site-inv-setup-empty">
                {rows.length === 0
                  ? 'No products are assigned to this client yet — use "Add product from Inventory Master" to get started.'
                  : 'No products match the current filters.'}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-[1]">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-600">
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Default Unit</th>
                    <th className="px-3 py-2 text-right">Measure</th>
                    <th className="px-3 py-2">Default Serving Unit</th>
                    <th className="px-3 py-2 text-right">Yield (servings/unit)</th>
                    <th className="px-3 py-2">Yield Source</th>
                    <th className="px-3 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRows.map(r => {
                    const e = edits[r.product_id];
                    const isEditing = !!e;
                    return (
                    <tr key={r.id} className="hover:bg-slate-50/60 align-top" data-testid={`site-inv-setup-row-${r.product_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{r.product_name}</div>
                        <div className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5 mt-0.5">
                          {r.product_code && <span className="font-mono">{r.product_code}</span>}
                          {r.brand && <span>· {r.brand}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {r.category || '—'}
                        {r.subcategory && <div className="text-slate-400">{r.subcategory}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700" data-testid={`site-inv-setup-count-unit-${r.product_id}`}>
                        {r.default_unit || '—'}
                        {r.default_unit_is_site && <div className="text-[10px] text-slate-400">Site count unit</div>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {/* iter340 v8 — Measure is read-only here; it's
                            auto-derived from the chosen Default Serving
                            Unit (Pint → 568ml, Glass → 150ml, etc.) for a
                            volume product. 2026-11 fix — the raw number is
                            formatted per the product's own measure type
                            (ml / g / n-a) instead of always appending
                            " ml", which mislabelled e.g. a 40g snack
                            packet as "40 ml". */}
                        {fmtMl(r.effective_measure_ml, r.measure_type)}
                        {r.override_measure_ml != null && r.master_measure_ml != null && (
                          <div className="text-[10px] text-slate-400 line-through">
                            master: {fmtMl(r.master_measure_ml, r.measure_type)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Select
                            value={e.measure_label || '__none__'}
                            onValueChange={v => updateEdit(r.product_id, 'measure_label', v === '__none__' ? '' : v)}
                          >
                            <SelectTrigger className="h-7 text-xs" data-testid={`site-inv-edit-serving-unit-${r.product_id}`}>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— None —</SelectItem>
                              {measureDefs.map(d => (
                                <SelectItem key={d.id} value={d.name}>
                                  {d.name}{d.default_ml != null ? ` (${Number(d.default_ml).toFixed(0)}ml)` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-slate-700" data-testid={`site-inv-setup-serving-unit-${r.product_id}`}>
                              {(r.override_measure_label || r.master_measure_label) || <span className="text-slate-400">—</span>}
                            </span>
                            <Badge
                              variant={r.measure_source === 'Site-Specific Override' ? 'default' : 'outline'}
                              className={`text-[10px] ${r.measure_source === 'Site-Specific Override' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' : 'bg-slate-50 text-slate-600'}`}
                              data-testid={`site-inv-setup-measure-source-${r.product_id}`}
                            >
                              {r.measure_source}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {isEditing ? (
                          <Input
                            type="number"
                            step="any"
                            min="0"
                            value={e.nominal_servings}
                            onChange={ev => updateEdit(r.product_id, 'nominal_servings', ev.target.value)}
                            className="h-7 text-right tabular-nums"
                            placeholder="per unit"
                            data-testid={`site-inv-edit-servings-${r.product_id}`}
                          />
                        ) : (
                          <>
                            {fmtServings(r.effective_servings, r.measure_type)}
                            {r.override_servings != null && r.master_servings != null && (
                              <div className="text-[10px] text-slate-400 line-through">
                                master: {fmtServings(r.master_servings, r.measure_type)}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          variant={r.yield_source === 'Site-Specific Override' ? 'default' : 'outline'}
                          className={`text-[10px] ${r.yield_source === 'Site-Specific Override' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' : 'bg-slate-50 text-slate-600'}`}
                          data-testid={`site-inv-setup-yield-source-${r.product_id}`}
                        >
                          {r.yield_source}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <div className="flex flex-col gap-1 items-end">
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              disabled={!e.dirty || e.saving}
                              onClick={() => saveEdit(r)}
                              data-testid={`site-inv-save-${r.product_id}`}
                            >
                              {e.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                            </Button>
                            {r.any_override && (
                              <button
                                type="button"
                                className="text-[10px] text-amber-700 hover:underline disabled:opacity-50"
                                disabled={e.saving}
                                onClick={() => resetToMaster(r)}
                                data-testid={`site-inv-reset-${r.product_id}`}
                              >
                                Reset to master
                              </button>
                            )}
                            <button
                              type="button"
                              className="text-[10px] text-slate-500 hover:underline disabled:opacity-50"
                              disabled={e.saving}
                              onClick={() => cancelEdit(r.product_id)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => beginEdit(r)}
                            data-testid={`site-inv-edit-${r.product_id}`}
                          >
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
            <span>
              Showing <strong>{filteredRows.length}</strong> of {rows.length} assigned products
              {rows.some(r => r.any_override) && (
                <> · <span className="text-amber-700">{rows.filter(r => r.any_override).length} with site-specific overrides</span></>
              )}
            </span>
            <span>
              Click <strong>Edit</strong> on any row to apply or reset a Site-Specific Override.
            </span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Re-use the existing AddFromMaster modal (online + offline aware). */}
      <AddFromMasterModal
        open={addFromMasterOpen}
        onOpenChange={setAddFromMasterOpen}
        siteId={siteId}
        assignedProductIds={assignedProductIds}
        onAssigned={handleAssigned}
        offlineProducts={offlineProductsForModal}
        onOfflineAssign={isOfflineMode && onOfflineMasterAssign
          ? async (product) => {
              await onOfflineMasterAssign(product);
              load();
            }
          : null}
      />
    </>
  );
}
